import { Trophy } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export type RankTier = "gold" | "podium" | "top10" | "none";

export type RankBadgeProps = {
  tier: RankTier;
  position?: number;
  total?: number;
  size?: "sm" | "md" | "lg";
  className?: string;
};

const tierClass: Record<RankTier, string> = {
  gold: "bg-rank-gold text-[#1f1600] ring-2 ring-rank-gold/30",
  podium: "bg-rank-podium text-white",
  top10: "bg-rank-top10 text-white",
  none: "bg-rank-none/20 text-fg-muted",
};
const sizeClass = { sm: "h-5 min-w-5 px-1.5 text-[11px]", md: "h-6 min-w-6 px-2 text-xs", lg: "h-8 min-w-8 px-2.5 text-sm" };

export function rankTier(position: number | null | undefined): RankTier {
  if (!position || position < 1) return "none";
  if (position === 1) return "gold";
  if (position <= 3) return "podium";
  if (position <= 10) return "top10";
  return "none";
}

export function RankBadge({ tier, position, total, size = "md", className }: RankBadgeProps) {
  const label = position ? `Ranked ${position}${total ? ` of ${total}` : ""}` : "Unranked";
  return (
    <span
      className={cn("num inline-flex items-center justify-center gap-1 rounded-full font-semibold leading-none", tierClass[tier], sizeClass[size], className)}
      aria-label={label}
      title={label}
    >
      {tier === "gold" && <Trophy className={size === "lg" ? "size-4" : "size-3"} aria-hidden />}
      {position ? (
        <>
          #{position}
          {total ? <span className="font-normal opacity-70">/{total}</span> : null}
        </>
      ) : (
        "–"
      )}
    </span>
  );
}
